import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {signUpApi} from "../../api/signup.js";
import {useNavigate, useParams} from "react-router-dom";
import {taxAuthApi} from "../../api/taxAuth.js";
import * as yup from "yup";
import {Card, Col, Container, InputGroup, Row, Spinner} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import {Formik} from "formik";
import HeroSection from "../../components/HeroSection.jsx";

function OnboardTaxAuthorityPage(){
    // get "state" url query param
    const {inviteKey} = useParams();
    const nav = useNavigate();
    const [pageState, setPageState] = useState({
        loading: false,
        onboardingInfo: null,
        onboardingKeyInvalid: false
    });

    const schema = yup.object().shape({
        firstName: yup.string().required('First Name is required'),
        lastName: yup.string().required('Last Name is required'),
        password: yup.string()
            .required('Password is required')
            .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
                "Password must be at least 8 characters, with an uppercase letter, lowercase letter, and a number."
            ),
        passwordConfirm: yup.string()
            .required('Password confirm is required')
            .oneOf([yup.ref('password')], 'Passwords must match'),
        address: yup.string().required('Address is required'),
        city: yup.string().required('City is required'),
        postCode: yup.string().required('Postal Code is required'),
        phoneNumber: yup.string().required('Phone number is required'),
        terms: yup.bool().required().oneOf([true], 'Terms must be accepted'),
        financialContactName: yup.string().required('Financial Contact Name is required'),
        financialContactPhone: yup.string().required('Financial Contact Phone is required'),
        financialContactEmail: yup.string().email("Must be a valid email").required('Financial Contact Email is required'),
        techContactName: yup.string().required('Tech Contact Name is required'),
        techContactPhone: yup.string().required('Tech Contact Phone is required'),
        techContactEmail: yup.string().email("Must be a valid email").required('Tech Contact Email is required'),
        taxCertificatePrice: yup.string().required("Tax Certificate Price is required"),

    });

    useEffect(() => {
        setPageState(prevState => {
            return {...prevState, loading: true};
        });
        const getOnboardingInfo = async () => {
            try {
            const info = await taxAuthApi.getOnboardingInfo(inviteKey);
                setPageState(prevState => {
                    return {...prevState, loading: false, onboardingInfo: info};
                });
            } catch(e) {
                console.log(e);
                setPageState(prevState => {
                    return {...prevState, loading: false, onboardingKeyInvalid: true};
                });
            }
            
            
        }
        getOnboardingInfo();
    }, [inviteKey]);

    if (pageState.onboardingKeyInvalid){
        return (
            <div className="dashboard-shell" style={{background: "#f8fafc"}}>
                <Container className="py-4">
                    <HeroSection
                        accent="taxauth"
                        eyebrow="Onboarding"
                        title="Invalid onboarding key"
                        subtitle="The link may have expired or already been used."
                    />
                    <Card className="dashboard-panel mt-3">
                        <Card.Body className="text-center">
                            <p className="mb-4">
                                The onboarding key you provided is invalid. It has either expired or has already been used to onboard a tax authority.
                            </p>
                            <Button onClick={() => {nav('/login')}}>Go to Log In Now</Button>
                        </Card.Body>
                    </Card>
                </Container>
            </div>
        )
    }
    else if (pageState.formSubmitted){
        return (
            <div className="dashboard-shell" style={{background: "#f8fafc"}}>
                <Container className="py-4">
                    <HeroSection
                        accent="taxauth"
                        eyebrow="Onboarding"
                        title="Sign-up complete"
                        subtitle="Your municipality account has been created. Next, sign in to finish setting up payouts."
                    />
                    <Card className="dashboard-panel mt-3">
                        <Card.Body className="text-center">
                            <p className="mb-4">
                                You have successfully completed creating your account. Please sign in to finish setting up your bank account and begin accepting orders.
                            </p>
                            <Button onClick={() => {nav('/login')}}>Go to Log In Now</Button>
                        </Card.Body>
                    </Card>
                </Container>
            </div>

        )
    } else {
        return (
            <div className="dashboard-shell" style={{background: "#f8fafc"}}>
                <Container className="py-4">
                    <HeroSection
                        accent="taxauth"
                        eyebrow="Municipality Onboarding"
                        title="Get set up with BC Tax Certificates"
                        subtitle="Complete your onboarding details so we can activate your account and start processing tax certificate orders."
                    />
                    {pageState.loading ? (
                        <div className="d-flex align-items-center justify-content-center py-5">
                            <Spinner/>
                        </div>
                    ):(
                        <Card className="dashboard-panel mt-3">
                            <Card.Body>
                                <p className="text-muted">
                                    Provide your contact, mailing, and technical details below. This information helps us configure payouts and set up your integration.
                                </p>
                                <Formik
                                    validationSchema={schema}
                                    onSubmit={async (values, { setSubmitting }) => {
                                        try {
                                            setSubmitting(true);
                                            await signUpApi.taxAuthSignup(inviteKey, values.firstName, values.lastName, values.password, values.phoneNumber,
                                                values.address, values.city, values.postCode, values.financialContactName, values.financialContactPhone,
                                                values.financialContactEmail, values.taxCertificatePrice, values.techContactName, values.techContactPhone,
                                                values.techContactEmail, null);
                                        } finally {
                                            setSubmitting(false);
                                            setPageState(prevState => {
                                                return {...prevState, formSubmitted : true}
                                            });
                                        }

                                    }}
                                    initialValues={{
                                        firstName: '',
                                        lastName: '',
                                        address: '',
                                        password: '',
                                        passwordConfirm: '',
                                        phoneNumber: '',
                                        city: '',
                                        postCode: '',
                                        terms: false,
                                        taxCertificatePrice: null,
                                        techContactName: '',
                                        techContactEmail: '',
                                        techContactPhone: '',
                                        financialContactName: '',
                                        financialContactEmail: '',
                                        financialContactPhone: '',
                                    }}
                                >
                                    {({ handleSubmit, handleChange, setFieldValue, touched, values, isSubmitting, errors }) => {
                                        return (
                                            <Form noValidate onSubmit={handleSubmit}>
                                                <Row className="mb-3">
                                                    <h3>User Information</h3>
                                                    <hr/>
                                                    <Form.Group as={Col} md="4" controlId="firstName">
                                                        <Form.Label>First name</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="firstName"
                                                                value={values.firstName}
                                                                onChange={handleChange}
                                                                isInvalid={touched.firstName && !!errors.firstName}
                                                            />
                                                            {touched.firstName && (<Form.Control.Feedback type="invalid">
                                                                {errors.firstName}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="lastName">
                                                        <Form.Label>Last name</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="lastName"
                                                                value={values.lastName}
                                                                onChange={handleChange}
                                                                isInvalid={touched.lastName && !!errors.lastName}
                                                            />
                                                            {touched.lastName && (<Form.Control.Feedback type="invalid">
                                                                {errors.lastName}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>

                                                </Row>
                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md="4" controlId="email">
                                                        <Form.Label>Email</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="email"
                                                                value={pageState?.onboardingInfo?.emailAddress}
                                                                readOnly={true}
                                                                disabled={true}
                                                            />

                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="password">
                                                        <Form.Label>Password</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="password"
                                                                name="password"
                                                                value={values.password}
                                                                onChange={handleChange}
                                                                isInvalid={touched.password && !!errors.password}
                                                            />
                                                            {touched.password && (<Form.Control.Feedback type="invalid">
                                                                {errors.password}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="passwordConfirm">
                                                        <Form.Label>Confirm Password</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="password"
                                                                name="passwordConfirm"
                                                                value={values.passwordConfirm}
                                                                onChange={handleChange}
                                                                isInvalid={touched.passwordConfirm && !!errors.passwordConfirm}
                                                            />
                                                            {touched.passwordConfirm && (<Form.Control.Feedback type="invalid">
                                                                {errors.passwordConfirm}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Row>
                                                <h3>Mailing Address</h3>
                                                <hr/>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md="4" controlId="organizationName">
                                                        <Form.Label>Municipality Name</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                readOnly={true}
                                                                disabled={true}
                                                                type="text"
                                                                name="organizationName"
                                                                value={pageState?.onboardingInfo?.taxAuthName}
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="address">
                                                        <Form.Label>Address</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="address"
                                                            value={values.address}
                                                            onChange={handleChange}
                                                            isInvalid={touched.address && !!errors.address}
                                                        />

                                                        {touched.address && (<Form.Control.Feedback type="invalid">
                                                            {errors.address}
                                                        </Form.Control.Feedback>)}
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="phoneNumber">
                                                        <Form.Label>Phone Number</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="phoneNumber"
                                                                name="phoneNumber"
                                                                value={values.phoneNumber}
                                                                onChange={handleChange}
                                                                isInvalid={touched.phoneNumber && !!errors.phoneNumber}
                                                            />
                                                            {touched.phoneNumber && (<Form.Control.Feedback type="invalid">
                                                                {errors.phoneNumber}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Row>
                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md="4" controlId="city">
                                                        <Form.Label>City</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="city"
                                                            value={values.city}
                                                            onChange={handleChange}
                                                            isInvalid={touched.city && !!errors.city}
                                                        />

                                                        {touched.city && (<Form.Control.Feedback type="invalid">
                                                            {errors.city}
                                                        </Form.Control.Feedback>)}
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="province">
                                                        <Form.Label>Province</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                readOnly={true}
                                                                disabled={true}
                                                                type="text"
                                                                name="province"
                                                                value="BC"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="postCode">
                                                        <Form.Label>Postal Code</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="postCode"
                                                            value={values.zip}
                                                            onChange={handleChange}
                                                            isInvalid={touched.postCode && !!errors.postCode}
                                                        />

                                                        {touched.postCode && (<Form.Control.Feedback type="invalid">
                                                            {errors.postCode}
                                                        </Form.Control.Feedback>)}
                                                    </Form.Group>
                                                </Row>
                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md="4" controlId="country">
                                                        <Form.Label>Country</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                readOnly={true}
                                                                disabled={true}
                                                                type="text"
                                                                name="country"
                                                                value="Canada"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Row>
                                                <h3>Financial Info</h3>
                                                <hr/>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md="4" controlId="financialContactName">
                                                        <Form.Label>Financial Contact Name</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="financialContactName"
                                                                value={values.financialContactName}
                                                                onChange={handleChange}
                                                                isInvalid={touched.financialContactName && !!errors.financialContactName}
                                                            />
                                                            {touched.financialContactName && (<Form.Control.Feedback type="invalid">
                                                                {errors.financialContactName}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="financialContactPhone">
                                                        <Form.Label>Financial Contact Phone</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="financialContactPhone"
                                                                value={values.financialContactPhone}
                                                                onChange={handleChange}
                                                                isInvalid={touched.financialContactPhone && !!errors.financialContactPhone}
                                                            />
                                                            {touched.financialContactPhone && (<Form.Control.Feedback type="invalid">
                                                                {errors.financialContactPhone}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="financialContactEmail">
                                                        <Form.Label>Financial Contact Email</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="financialContactEmail"
                                                                value={values.financialContactEmail}
                                                                onChange={handleChange}
                                                                isInvalid={touched.financialContactEmail && !!errors.financialContactEmail}
                                                            />
                                                            {touched.financialContactEmail && (<Form.Control.Feedback type="invalid">
                                                                {errors.financialContactEmail}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Row>
                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md="4" controlId="taxCertificatePrice">
                                                        <Form.Label>Tax Certificate Price</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <InputGroup.Text>$</InputGroup.Text>
                                                            <Form.Control
                                                                type="number"
                                                                name="taxCertificatePrice"
                                                                value={values.taxCertificatePrice}
                                                                onChange={handleChange}
                                                                isInvalid={touched.taxCertificatePrice && !!errors.taxCertificatePrice}
                                                            />
                                                            {touched.taxCertificatePrice && (<Form.Control.Feedback type="invalid">
                                                                {errors.taxCertificatePrice}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Row>
                                                <h3>Tech Contact Info</h3>
                                                <hr/>
                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md="4" controlId="techContactName">
                                                        <Form.Label>Tech Contact Name</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="techContactName"
                                                                value={values.techContactName}
                                                                onChange={handleChange}
                                                                isInvalid={touched.techContactName && !!errors.techContactName}
                                                            />
                                                            {touched.techContactName && (<Form.Control.Feedback type="invalid">
                                                                {errors.techContactName}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="techContactPhone">
                                                        <Form.Label>Tech Contact Phone</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="techContactPhone"
                                                                value={values.techContactPhone}
                                                                onChange={handleChange}
                                                                isInvalid={touched.techContactPhone && !!errors.techContactPhone}
                                                            />
                                                            {touched.techContactPhone && (<Form.Control.Feedback type="invalid">
                                                                {errors.techContactPhone}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md="4" controlId="techContactEmail">
                                                        <Form.Label>Tech Contact Email</Form.Label>
                                                        <InputGroup hasValidation>
                                                            <Form.Control
                                                                type="text"
                                                                name="techContactEmail"
                                                                value={values.techContactEmail}
                                                                onChange={handleChange}
                                                                isInvalid={touched.techContactEmail && !!errors.techContactEmail}
                                                            />
                                                            {touched.techContactEmail && (<Form.Control.Feedback type="invalid">
                                                                {errors.techContactEmail}
                                                            </Form.Control.Feedback>)}
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Row>
                                                <Form.Group className="mb-3">
                                                    <Form.Check
                                                        required
                                                        name="terms"
                                                        label="Agree to terms and conditions"
                                                        onChange={handleChange}
                                                        isInvalid={touched.terms && !!errors.terms}
                                                        feedback={errors.terms}
                                                        feedbackType="invalid"
                                                        id="validationFormik0"
                                                    />
                                                </Form.Group>
                                                <div className="d-flex justify-content-end">
                                                    <Button type="submit" disabled={isSubmitting}>Submit Registration</Button>
                                                </div>
                                            </Form>
                                        )}}
                                </Formik>
                            </Card.Body>
                        </Card>
                    )}
                </Container>
            </div>

        );
    }


}

export default observer(OnboardTaxAuthorityPage);
